import enJson from "../../web/public/locales/en.json";

/**
 * An English sentence that is ALSO its own i18next catalog key — this app's convention, which is
 * why `packages/web/public/locales/en.json` is largely an identity map. It is the key union of that
 * catalog, the same type web's `TranslationKey` is, so a mapper sentence reaches web's typed `t()`
 * without a cast, and a sentence that is not already in en.json is a compile error in
 * {@link ErrorCodeMapperEntry}.
 *
 * Distinct from `TranslatedString` (`packages/helpers/types/i18n.ts`), which is the OUTPUT of
 * translation. This is the INPUT — the untranslated key.
 */
export type CatalogKey = keyof typeof enJson;

/**
 * Marks a message as a translatable key. **Identity at runtime** — it returns the string unchanged,
 * typed as the literal catalog key it was given.
 *
 * It is NOT a translator, and react-i18next's real `t` must never be used here. Translation happens
 * on the web at render time, via `resolveErrorContent` -> `getTranslatedErrorMessage(code, params,
 * t)`, because only then are the `params`, the `count` for a plural swap, and the active language
 * known. Three things make a construction-time `t()` actively wrong in this app:
 *
 *   1. The catalog is fetched over HTTP and, since `i18n.init` is called with `ns: []`, the fetch
 *      does not even start until the first `useTranslation()` render — later than any module body.
 *   2. The language changes mid-session with no reload (`ProviderLanguage` calls `changeLanguage`
 *      ~2s into every session), so a value captured at import freezes at the boot language.
 *   3. `packages/helpers` depends only on `sanitize-html` and must never import i18next — it is
 *      shared with `packages/server`, which has no i18n layer at all.
 *
 * Named `t` so i18n extraction tooling would recognise these strings if one is ever added.
 */
export const t = <K extends CatalogKey>(key: K): K => key;

/**
 * Shape every entry below must satisfy, checked via `satisfies` rather than a direct type
 * annotation on `errorCodeMapper` — an annotation would widen every named property access
 * (`errorCodeMapper.LICENCE_EXPIRED`) to "possibly missing an optional field", when what's wanted is
 * the opposite: each entry keeps its own precise literal type for direct access, while still
 * structurally satisfying this interface (optional fields absent from a literal are simply absent,
 * not errors) wherever a function below asks for `ErrorCodeMapperEntry` in general — e.g. after a
 * `getEntryByErrorCode` lookup, where the semantic key is unknown and only the numeric code is in
 * hand.
 */
export interface ErrorCodeMapperEntry {
  errorCode: number;
  /** Written `t("…")`; a sentence that is not a key in en.json is a compile error. See {@link CatalogKey}. */
  errorMessage: CatalogKey;
  /**
   * Only set on entries whose message is a sentence SWAP based on a `count` param (e.g. "This unit
   * is in use..." vs "One or more of the selected units are in use..."), never plain `{{param}}`
   * interpolation — see `getErrorMessageTemplate`. Also `t("…")`-wrapped.
   */
  errorMessagePlural?: CatalogKey;
  isDynamic: boolean;
  dynamicParams?: string[];
}
